import { useEffect, useRef } from 'react';

const GOOGLE_SCRIPT_ID = 'google-oauth-script';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const loadGoogleScript = () => {
  if (document.getElementById(GOOGLE_SCRIPT_ID)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google OAuth script'));
    document.body.appendChild(script);
  });
};

export const useGoogleOAuth = ({ onSuccess, onError }) => {
  const clientRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI || 'postmessage';

    const initializeClient = () => {
      if (!window.google?.accounts?.oauth2) {
        if (!cancelled) onError?.(new Error('Google OAuth is unavailable.'));
        return;
      }

      // Init code-client (idempotent – overskriver ref ved re-render)
      clientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        ux_mode: 'popup',
        redirect_uri: redirectUri,
        callback: (response) => {
          if (response?.code) {
            onSuccess?.(response.code);
          } else if (response?.error) {
            onError?.(new Error(response.error));
          } else {
            onError?.(new Error('No authorization code returned.'));
          }
        },
      });
    };

    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      // Ikke forsøk å laste script uten klient-ID konfigurert
      return () => {
        cancelled = true;
      };
    }

    if (window.google?.accounts?.oauth2) {
      initializeClient();
    } else {
      loadGoogleScript()
        .then(() => {
          if (!cancelled) initializeClient();
        })
        .catch((error) => {
          if (!cancelled) onError?.(error);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [onSuccess, onError]);

  const requestCode = () => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      onError?.(new Error('Google client ID is not configured.'));
      return;
    }
    if (clientRef.current) {
      clientRef.current.requestCode();
    } else {
      onError?.(new Error('Google OAuth client is not ready.'));
    }
  };

  return { requestCode };
};

export default useGoogleOAuth;
